import React, { useState } from "react"
import ScreenshotPreview from "../shared/ScreenshotPreview.jsx"
import { Link, useParams } from "react-router-dom"
import useExperiment from "../../hooks/useExperiment.js"
import useApi from "../../hooks/useApi.js"

const STATUS_PILL = {
  draft:             { bg: "#F8FAFC", fg: "#64748B" },
  pending_approval:  { bg: "#ECFDF5", fg: "#047857" },
  running:           { bg: "#F1F5F9", fg: "#475569" },
  paused:            { bg: "#F8FAFC", fg: "#475569" },
  completed:         { bg: "#D1FAE5", fg: "#047857" },
  archived:          { bg: "#F8FAFC", fg: "#64748B" }
}

const MONTHS = [ "JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC" ]

function formatDate(iso) {
  if (!iso) return ""
  const d = new Date(iso)
  return `${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}`
}

// M9/9g — scope-aware target meta. Same helper lives in ExperimentsPage.jsx.
function scopeLabel(t, experiment) {
  if (experiment.scope === "page_type") {
    return t("dashboard.experiments.scope_page_type", {
      page_type: experiment.page_type,
      pattern: experiment.page_pattern
    })
  }
  return experiment.page_path || experiment.page_pattern || ""
}

function metaLine(t, experiment) {
  switch (experiment.status) {
    case "completed": return t("dashboard.experiment_detail.meta_completed", { date: formatDate(experiment.completed_at) })
    case "running":
    case "paused":   return t("dashboard.experiment_detail.meta_started", { date: formatDate(experiment.started_at) })
    case "pending_approval": return t("dashboard.experiment_detail.meta_pending")
    case "draft":    return t("dashboard.experiment_detail.meta_draft", { date: formatDate(experiment.created_at) })
    case "archived": return t("dashboard.experiment_detail.meta_archived")
    default:         return ""
  }
}

function StatusPill({ t, status }) {
  const pill = STATUS_PILL[status] || STATUS_PILL.archived
  return (
    <span
      className="inline-flex items-center gap-2 text-[11px] font-medium font-mono rounded-pill"
      style={{ background: pill.bg, color: pill.fg, padding: "3px 10px" }}
    >
      <span className="w-[6px] h-[6px] rounded-full" style={{ background: pill.fg, opacity: 0.55 }} />
      {t(`dashboard.experiments.status_${status}`)}
    </span>
  )
}

function VariantMetric({ label, value }) {
  return (
    <div>
      <p
        className="font-mono text-[10px] text-ink-tertiary mb-1"
        style={{ letterSpacing: "0.10em" }}
      >
        {label}
      </p>
      <p
        className="text-[18px] font-medium text-ink-primary"
        style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}
      >
        {value}
      </p>
    </div>
  )
}

function valuePreview(value) {
  if (value == null) return "—"
  if (typeof value === "string") return value
  try { return JSON.stringify(value) } catch { return String(value) }
}

function DomChangesList({ t, changes }) {
  if (!changes || changes.length === 0) {
    return (
      <p className="text-[13px] text-ink-tertiary" style={{ lineHeight: 1.55 }}>
        {t("dashboard.experiment_detail.variant_no_changes")}
      </p>
    )
  }
  return (
    <ul className="space-y-2">
      {changes.map((c, i) => (
        <li
          key={i}
          className="font-mono text-[11px] text-ink-secondary border-l-2 border-rule pl-3 py-1"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          <div className="text-ink-primary">{c.selector}</div>
          <div className="text-ink-faint mt-0.5">
            <span className="text-ai">{c.action}</span>
            {c.value !== undefined && <span> · {valuePreview(c.value)}</span>}
          </div>
        </li>
      ))}
    </ul>
  )
}

function VariantCard({ t, variant, isWinner, appliedNativelyAt }) {
  // Derive live CVR from counts. The persisted `conversion_rate` column is
  // only updated by EvaluateExperimentsJob, so it stays at 0 for running
  // experiments. The list/Overview both derive the same way.
  const cvr = variant.impressions_count > 0
    ? variant.conversions_count / variant.impressions_count
    : 0
  return (
    <article className="bg-surface-card border border-rule rounded-card p-6 space-y-5">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[16px] font-medium tracking-[-0.015em] text-ink-primary">
            {variant.name}
          </h3>
          <div className="flex gap-2 mt-2">
            {variant.control && (
              <span
                className="font-mono text-[10px] text-ink-tertiary border border-rule px-2 py-[2px] rounded-pill"
                style={{ letterSpacing: "0.10em" }}
              >
                {t("dashboard.experiment_detail.variant_control_label")}
              </span>
            )}
            {isWinner && (
              <span
                className="font-mono text-[10px] font-medium rounded-pill"
                style={{
                  background: "#D1FAE5",
                  color: "#047857",
                  padding: "2px 8px",
                  letterSpacing: "0.10em"
                }}
              >
                {appliedNativelyAt
                  ? t("dashboard.experiment_detail.variant_winner_native_label")
                  : t("dashboard.experiment_detail.variant_winner_label")}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-4">
        <VariantMetric
          label={t("dashboard.experiment_detail.variant_metric_impressions")}
          value={variant.impressions_count.toLocaleString()}
        />
        <VariantMetric
          label={t("dashboard.experiment_detail.variant_metric_conversions")}
          value={variant.conversions_count.toLocaleString()}
        />
        <VariantMetric
          label={t("dashboard.experiment_detail.variant_metric_rate")}
          value={`${(cvr * 100).toFixed(1)}%`}
        />
      </div>

      {variant.ai_reasoning && (
        <div>
          <p
            className="font-mono text-[10px] text-ai mb-2"
            style={{ letterSpacing: "0.10em" }}
          >
            {t("dashboard.experiment_detail.variant_ai_reasoning_label")}
          </p>
          <p className="text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
            {variant.ai_reasoning}
          </p>
        </div>
      )}

      <div>
        <p
          className="font-mono text-[10px] text-ink-tertiary mb-2"
          style={{ letterSpacing: "0.10em" }}
        >
          {t("dashboard.experiment_detail.variant_dom_changes_label")}
        </p>
        <DomChangesList t={t} changes={variant.dom_changes} />
      </div>
    </article>
  )
}

function ActionButton({ children, onClick, disabled, variant = "secondary" }) {
  const base = "rounded-button text-[13px] font-medium disabled:opacity-60 transition-colors"
  const styles = {
    primary:   "bg-brand text-ink-on-dark px-4 py-2",
    secondary: "bg-surface-card border border-rule text-ink-primary px-3 py-[6px]",
    danger:    "text-ink-tertiary hover:text-failure px-3 py-[6px]"
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} ${styles[variant]}`}>
      {children}
    </button>
  )
}

function ActionBar({ t, experiment, onAction, busy }) {
  const status = experiment.status
  const post = (op) => () => onAction(op)

  if (status === "archived") return null

  const isBusy = (op) => busy === op
  const label = (op, fallback) => isBusy(op) ? t("dashboard.experiment_detail.action_busy") : fallback

  return (
    <div className="flex flex-wrap gap-3 items-center">
      {(status === "pending_approval" || status === "draft") && (
        <>
          <ActionButton variant="primary" onClick={post("approve")} disabled={!!busy}>
            {label("approve", t("dashboard.experiment_detail.action_approve"))}
          </ActionButton>
          <ActionButton variant="danger" onClick={post("archive")} disabled={!!busy}>
            {label("archive", t("dashboard.experiment_detail.action_dismiss"))}
          </ActionButton>
        </>
      )}
      {status === "running" && (
        <>
          <ActionButton onClick={post("pause")} disabled={!!busy}>
            {label("pause", t("dashboard.experiment_detail.action_pause"))}
          </ActionButton>
          <ActionButton variant="primary" onClick={post("complete")} disabled={!!busy}>
            {label("complete", t("dashboard.experiment_detail.action_complete"))}
          </ActionButton>
        </>
      )}
      {status === "paused" && (
        <>
          <ActionButton variant="primary" onClick={post("approve")} disabled={!!busy}>
            {label("approve", t("dashboard.experiment_detail.action_resume"))}
          </ActionButton>
          <ActionButton onClick={post("complete")} disabled={!!busy}>
            {label("complete", t("dashboard.experiment_detail.action_complete"))}
          </ActionButton>
          <ActionButton variant="danger" onClick={post("archive")} disabled={!!busy}>
            {label("archive", t("dashboard.experiment_detail.action_archive"))}
          </ActionButton>
        </>
      )}
      {status === "completed" && (
        <ActionButton variant="danger" onClick={post("archive")} disabled={!!busy}>
          {label("archive", t("dashboard.experiment_detail.action_archive"))}
        </ActionButton>
      )}
    </div>
  )
}

// M9/9e — "Check if live in your code." After a winner is declared, the
// snippet keeps serving it to 100% of visitors. This panel lets the owner
// confirm when they've ported the change natively so the snippet can stop.
// Result verdicts:
//   :adopted       → backend stamped applied_natively_at; pill updates on refresh
//   :not_adopted   → owner hasn't ported yet; snippet keeps serving
//   :indeterminate → can't tell (fetch failed, selector missing, or non-text/attr action)
function AdoptionPanel({ t, experiment, onRefresh }) {
  const api = useApi()
  const [busy, setBusy] = useState(false)
  const [verdict, setVerdict] = useState(null)
  const [error, setError] = useState(null)

  const check = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await api.post(`/api/v1/shop/experiments/${experiment.id}/check_adoption`)
      setVerdict(res.adoption_status)
      if (res.adoption_status === "adopted") await onRefresh()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (experiment.applied_natively_at) {
    return (
      <p
        className="font-mono text-[11px] text-winner"
        style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.05em" }}
      >
        {t("dashboard.experiment_detail.adoption_applied_natively", {
          date: formatDate(experiment.applied_natively_at)
        })}
      </p>
    )
  }

  return (
    <section className="space-y-3">
      <p
        className="font-mono text-[10px] text-ink-tertiary"
        style={{ letterSpacing: "0.15em" }}
      >
        {t("dashboard.experiment_detail.adoption_eyebrow")}
      </p>
      <p className="text-sm text-ink-secondary" style={{ lineHeight: 1.55 }}>
        {t("dashboard.experiment_detail.adoption_lede")}
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <ActionButton onClick={check} disabled={busy}>
          {busy
            ? t("dashboard.experiment_detail.adoption_checking")
            : t("dashboard.experiment_detail.adoption_check_button")}
        </ActionButton>
        {verdict === "not_adopted" && (
          <span className="text-[13px] text-ink-secondary">
            {t("dashboard.experiment_detail.adoption_not_adopted")}
          </span>
        )}
        {verdict === "indeterminate" && (
          <span className="text-[13px] text-ink-secondary">
            {t("dashboard.experiment_detail.adoption_indeterminate")}
          </span>
        )}
      </div>
      {error && <p className="text-sm text-failure">{error}</p>}
    </section>
  )
}

function ExportPanel({ t, experimentId }) {
  const api = useApi()
  const [payload, setPayload] = useState(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await api.get(`/api/v1/shop/experiments/${experimentId}/export`)
      setPayload(data)
    } catch (err) { setError(err.message) }
    finally { setLoading(false) }
  }

  const copy = async () => {
    if (!payload) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard unavailable */ }
  }

  if (!payload) {
    return (
      <ActionButton variant="primary" onClick={load} disabled={loading}>
        {loading ? t("dashboard.experiment_detail.action_busy") : t("dashboard.experiment_detail.action_export")}
      </ActionButton>
    )
  }

  return (
    <section className="space-y-3">
      <p
        className="font-mono text-[10px] text-ai"
        style={{ letterSpacing: "0.15em" }}
      >
        {t("dashboard.experiment_detail.export_eyebrow")}
      </p>
      <p className="text-sm text-ink-secondary" style={{ lineHeight: 1.55 }}>
        {t("dashboard.experiment_detail.export_lede")}
      </p>
      <div className="bg-surface-card border border-rule rounded-card">
        <div className="flex items-center justify-between px-4 py-2 border-b border-rule">
          <span
            className="font-mono text-[10px] text-ink-tertiary"
            style={{ letterSpacing: "0.10em" }}
          >
            JSON
          </span>
          <button
            type="button"
            onClick={copy}
            className="font-mono text-[11px] text-ai font-medium"
          >
            {copied ? t("dashboard.experiment_detail.export_copied") : t("dashboard.experiment_detail.export_copy")}
          </button>
        </div>
        <pre
          className="px-4 py-3 text-[12px] text-ink-primary overflow-x-auto font-mono"
          style={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.5 }}
        >
          {JSON.stringify(payload, null, 2)}
        </pre>
      </div>
      {error && <p className="text-sm text-failure">{error}</p>}
    </section>
  )
}

function ConversionMissingPanel({ t }) {
  return (
    <section
      className="rounded-card border p-6 space-y-3"
      style={{ background: "#FEF3C7", borderColor: "#FCD34D" }}
    >
      <p
        className="font-mono text-[10px] text-ink-tertiary"
        style={{ letterSpacing: "0.15em" }}
      >
        {t("dashboard.experiment_detail.conversion_missing_eyebrow")}
      </p>
      <h2
        className="text-[22px] font-medium text-ink-primary"
        style={{ letterSpacing: "-0.03em" }}
      >
        {t("dashboard.experiment_detail.conversion_missing_headline")}
      </h2>
      <p className="text-[13px] text-ink-primary" style={{ lineHeight: 1.55 }}>
        {t("dashboard.banners.conversion_missing_experiment_detail")}
      </p>
      <Link
        to="/settings"
        className="inline-block text-[13px] font-medium text-ai hover:underline"
      >
        {t("dashboard.banners.conversion_missing_cta")}
      </Link>
    </section>
  )
}

// M9/9h — cold-start experiments are evaluated against an owner-facing
// threshold stored on the row (min_impressions + min_relative_lift),
// not MDE. Same UI shape as DataCollectionPanel but reads different
// fields and uses different copy — MDE math doesn't apply here.
function ColdStartDataCollectionPanel({ t, experiment }) {
  const threshold = experiment.cold_start_win_threshold || {}
  const required = threshold.min_impressions
  const lift = Math.round((threshold.min_relative_lift ?? 0.15) * 100)

  return (
    <section className="bg-surface-card border border-rule rounded-card p-6 space-y-4">
      <p
        className="font-mono text-[10px] text-ai"
        style={{ letterSpacing: "0.15em" }}
      >
        {t("dashboard.experiment_detail.cold_start_eyebrow")}
      </p>
      <h2
        className="text-[22px] font-medium text-ink-primary"
        style={{ letterSpacing: "-0.03em" }}
      >
        {t("dashboard.experiment_detail.cold_start_headline")}
      </h2>
      <p className="text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
        {t("dashboard.experiment_detail.cold_start_lede", {
          required: required ? required.toLocaleString() : "—",
          lift: lift
        })}
      </p>
      <ul className="border-t border-rule">
        {experiment.variants.map((v) => {
          const remaining = required ? Math.max(0, required - v.impressions_count) : null
          return (
            <li
              key={v.id}
              className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-4 py-3 border-b border-rule items-center"
            >
              <span className="text-[14px] text-ink-primary">{v.name}</span>
              <span
                className="font-mono text-[12px] text-ink-secondary"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {required
                  ? `${v.impressions_count.toLocaleString()} / ${required.toLocaleString()}`
                  : v.impressions_count.toLocaleString()}
              </span>
              <span
                className="font-mono text-[11px] text-ink-tertiary"
                style={{ letterSpacing: "0.05em", fontVariantNumeric: "tabular-nums" }}
              >
                {remaining && remaining > 0
                  ? t("dashboard.experiment_detail.data_collection_to_go", {
                      count: remaining.toLocaleString()
                    })
                  : t("dashboard.experiment_detail.data_collection_ready")}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

// M9/9i — reads `sample_size_reason` off the mde payload so the "we're
// collecting" state, the "control has no visits" state, and the
// "control has visits but zero conversions" state get distinct copy.
// Pre-9i all three collapsed onto one generic lede, which made the
// zero-baseline case (usually a misconfigured conversion rule) invisible.
function DataCollectionPanel({ t, experiment }) {
  const required = experiment.mde?.required_sample_size
  const threshold = experiment.mde?.relative_lift_threshold ?? 0.20
  const reason = experiment.mde?.sample_size_reason
  const showTarget = reason === "collecting" && required

  const lede = reason === "zero_baseline_cvr"
    ? t("dashboard.experiment_detail.data_collection_lede_zero_baseline_cvr")
    : reason === "awaiting_baseline"
      ? t("dashboard.experiment_detail.data_collection_lede_awaiting_baseline")
      : t("dashboard.experiment_detail.data_collection_lede", {
          required: (required || 0).toLocaleString(),
          lift: Math.round(threshold * 100)
        })

  return (
    <section className="bg-surface-card border border-rule rounded-card p-6 space-y-4">
      <p
        className="font-mono text-[10px] text-ink-tertiary"
        style={{ letterSpacing: "0.15em" }}
      >
        {t("dashboard.experiment_detail.data_collection_eyebrow")}
      </p>
      <h2
        className="text-[22px] font-medium text-ink-primary"
        style={{ letterSpacing: "-0.03em" }}
      >
        {t("dashboard.experiment_detail.data_collection_headline")}
      </h2>
      <p className="text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
        {lede}
      </p>
      {reason === "zero_baseline_cvr" && (
        <Link
          to="/settings"
          className="inline-block text-[13px] font-medium text-ai hover:underline"
        >
          {t("dashboard.experiment_detail.data_collection_zero_baseline_cta")}
        </Link>
      )}
      <ul className="border-t border-rule">
        {experiment.variants.map((v) => (
          <li
            key={v.id}
            className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-4 py-3 border-b border-rule items-center"
          >
            <span className="text-[14px] text-ink-primary">{v.name}</span>
            <span
              className="font-mono text-[12px] text-ink-secondary"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {showTarget
                ? `${v.impressions_count.toLocaleString()} / ${required.toLocaleString()}`
                : v.impressions_count.toLocaleString()}
            </span>
            <span
              className="font-mono text-[11px] text-ink-tertiary"
              style={{ letterSpacing: "0.05em", fontVariantNumeric: "tabular-nums" }}
            >
              {showTarget
                ? (v.visits_remaining != null && v.visits_remaining > 0
                    ? t("dashboard.experiment_detail.data_collection_to_go", {
                        count: v.visits_remaining.toLocaleString()
                      })
                    : t("dashboard.experiment_detail.data_collection_ready"))
                : t("dashboard.experiment_detail.data_collection_waiting")}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default function ExperimentDetailPage({ t }) {
  const { id } = useParams()
  const { status, data: experiment, error, refresh } = useExperiment(id)
  const api = useApi()
  const [busy, setBusy] = useState(null)
  const [actionError, setActionError] = useState(null)

  const onAction = async (op) => {
    setBusy(op)
    setActionError(null)
    try {
      await api.post(`/api/v1/shop/experiments/${id}/${op}`)
      await refresh()
    } catch (err) { setActionError(err.message) }
    finally { setBusy(null) }
  }

  if (status === "loading") {
    return <p className="text-sm text-ink-tertiary">{t("dashboard.experiment_detail.loading")}</p>
  }
  if (status === "not_found") {
    return (
      <div className="space-y-4">
        <Link to="/experiments" className="font-mono text-[11px] text-ai">
          {t("dashboard.experiment_detail.back")}
        </Link>
        <p className="text-sm text-ink-tertiary">{t("dashboard.experiment_detail.not_found")}</p>
      </div>
    )
  }
  if (status === "error") {
    return <p className="text-sm text-failure">{error || t("dashboard.experiment_detail.error")}</p>
  }

  const winnerId = experiment.winner_variant_id
  const eyebrowStatus = t(`dashboard.experiments.status_${experiment.status}`).toUpperCase()

  return (
    <div className="space-y-10">
      <div>
        <Link
          to="/experiments"
          className="font-mono text-[11px] text-ai hover:underline"
          style={{ letterSpacing: "0.05em" }}
        >
          {t("dashboard.experiment_detail.back")}
        </Link>
      </div>

      <header className="space-y-4">
        <p
          className="font-mono text-[10px] text-ink-tertiary"
          style={{ letterSpacing: "0.15em", fontVariantNumeric: "tabular-nums" }}
        >
          {t("dashboard.experiment_detail.eyebrow", { status: eyebrowStatus })}
        </p>
        <div className="flex items-baseline gap-4 flex-wrap">
          <h1
            className="text-[28px] font-medium text-ink-primary"
            style={{ letterSpacing: "-0.035em" }}
          >
            {experiment.name}{experiment.name.endsWith(".") ? "" : "."}
          </h1>
          <StatusPill t={t} status={experiment.status} />
        </div>
        <p
          className="font-mono text-[12px] text-ink-tertiary"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {experiment.target_selector} · {scopeLabel(t, experiment)} · {metaLine(t, experiment)}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {experiment.archetype && (
            <p
              className="font-mono text-[10px] text-ai"
              style={{ letterSpacing: "0.15em" }}
            >
              {t("dashboard.experiment_detail.playbook_eyebrow", {
                archetype: experiment.archetype.replace(/_/g, " ").toUpperCase()
              })}
            </p>
          )}
          {experiment.origin === "cold_start" && (
            <p
              className="font-mono text-[10px] text-ai"
              style={{ letterSpacing: "0.15em" }}
            >
              {t("dashboard.experiment_detail.origin_cold_start_eyebrow")}
            </p>
          )}
        </div>
      </header>

      <ActionBar t={t} experiment={experiment} onAction={onAction} busy={busy} />
      {actionError && <p className="text-sm text-failure">{actionError}</p>}

      {/* M10/10d — before/after preview above the variant grid, on every
          status. Even archived experiments benefit from "what did we change?"
          reference shots. */}
      <ScreenshotPreview t={t} experiment={experiment} onRefresh={refresh} />

      {experiment.status === "running" && experiment.shop_has_conversion_rules === false ? (
        <ConversionMissingPanel t={t} />
      ) : experiment.status === "running" && experiment.origin === "cold_start" ? (
        <ColdStartDataCollectionPanel t={t} experiment={experiment} />
      ) : experiment.status === "running" && experiment.mde && !experiment.mde.sufficient_sample ? (
        <DataCollectionPanel t={t} experiment={experiment} />
      ) : null}

      <section
        className="grid gap-5"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}
      >
        {experiment.variants.map((v) => (
          <VariantCard
            key={v.id}
            t={t}
            variant={v}
            isWinner={v.id === winnerId}
            appliedNativelyAt={experiment.applied_natively_at}
          />
        ))}
      </section>

      {experiment.status === "completed" && winnerId && (
        <>
          <AdoptionPanel t={t} experiment={experiment} onRefresh={refresh} />
          <ExportPanel t={t} experimentId={experiment.id} />
        </>
      )}
    </div>
  )
}
